// apps/web/src/pages/ImageFilter.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import {
  FILTER_PRESETS,
  type FilterPresetId,
  getWebFilterStyle,
} from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';

type LocState = {
  sidebarData?: any;
  mediaItem?: { id: string; file: File; filterId?: FilterPresetId };
};

const SIGN: Record<FilterPresetId, string> = {
  none:   '◎',

  // subtle
  soft:   '▫️',
  warm:   '🔆',
  icy:    '❄️',
  pastel: '🎨',

  // medium
  sunny:  '☀️',
  frozen: '🧊',
  dusk:   '🌇',
  noir:   '⬛',
  gold:   '🥇',
  rose:   '🌹',
  teal:   '🌊',
  amber:  '🟧',
  film:   '🎞️',

  // strongest
  cinema: '🎬',
};

export default function ImageFilter() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: LocState };
  const { mediaItems, updateMediaItem } = useMediaPreview();
  const { t } = useTranslation();

  const mediaItem = state?.mediaItem || mediaItems.find(m => m.id === id);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Use existing preset on the item if present, otherwise 'none'
  const initialPreset = (mediaItem as any)?.filterId as FilterPresetId | undefined;
  const [filterId, setFilterId] = useState<FilterPresetId>(initialPreset ?? 'none');

  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!mediaItem?.file) return;
    const url = URL.createObjectURL(mediaItem.file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaItem?.file]);

  // Current preset styles (live preview)
  const { cssFilter, overlay } = useMemo(
    () => getWebFilterStyle(filterId),
    [filterId]
  );

  // Save: bake ONLY the preset (cssFilter + overlay) into a new image
  const handleSave = async () => {
    if (!imageUrl || !mediaItem) return;
    const src = await loadImage(imageUrl);

    const canvas = document.createElement('canvas');
    canvas.width = src.naturalWidth;
    canvas.height = src.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // draw with css-like filter
    ctx.filter = cssFilter ?? 'none';
    ctx.drawImage(src, 0, 0);

    // tint overlay (multiply for broad browser support; close to mobile feel)
    if (overlay?.tint && (overlay.opacity ?? 0) > 0) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = overlay.opacity!;
      ctx.fillStyle = overlay.tint!;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    const blob: Blob = await new Promise((res) =>
      canvas.toBlob(b => res(b!), 'image/jpeg', 0.92)
    );
    const file = new File([blob], `edited-${Date.now()}.jpg`, { type: 'image/jpeg' });

    // Persist: keep the chosen filterId on the item, replace file
    updateMediaItem({ ...mediaItem, file, filterId } as any);

    const targetId = mediaItem.id;

    navigate('/gallery-posting', {
      state: {
        sidebarData: state?.sidebarData,
        previewFile: file,
        previewFilename: file.name,
        editTargetId: targetId,
        isPreview: true,
      },
    });
  };

  const resetPreset = () => setFilterId('none');

  return (
    <div style={pageShell}>
      <div style={cardGrid}>
        {/* Sidebar: preset chooser + fixed actions at bottom */}
        <aside style={sidebar}>
          <div style={sidebarHeader}>
            <div style={sidebarTitle}>{t('imageFilter.sidebar.title')}</div>
            <div style={sidebarSubtitle}>
              {t('imageFilter.sidebar.subtitle', 'Choose a mood for your photo')}
            </div>
          </div>

          {/* Scrollable filter list (only this should scroll) */}
          <div style={filterScrollArea}>
            {FILTER_PRESETS.map(p => {
              const active = filterId === p.id;
              const { overlay: sw, cssFilter: previewFilter } = getWebFilterStyle(p.id);

              return (
                <button
                  key={p.id}
                  onClick={() => setFilterId(p.id)}
                  style={{
                    ...filterTile,
                    ...(active ? filterTileActive : null),
                  }}
                  title={p.hint}
                >
                  <div style={filterIconBubble}>
                    <span style={{ fontSize: 20 }}>
                      {SIGN[p.id]}
                    </span>
                  </div>

                  <div style={filterTextCol}>
                    <div style={filterLabelRow}>
                      <span style={filterName}>{p.label}</span>
                      {active && (
                        <span style={filterActiveBadge}>
                          {t('imageFilter.badges.current', 'Current')}
                        </span>
                      )}
                    </div>
                    {p.hint && (
                      <div style={filterHint}>
                        {p.hint}
                      </div>
                    )}
                  </div>

                  {/* Tiny live preview chip */}
                  <div style={filterPreviewChip}>
                    <div
                      style={{
                        ...filterPreviewChipInner,
                        filter: previewFilter ?? 'none',
                      }}
                    >
                      {!!sw?.tint && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: sw.tint,
                            opacity: sw.opacity ?? 0,
                            mixBlendMode: p.id === 'noir' ? 'multiply' : 'overlay',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Fixed action buttons at bottom of sidebar */}
          <div style={sidebarActions}>
            <button onClick={resetPreset} style={secondaryBtn}>
              🔄 {t('imageFilter.buttons.reset')}
            </button>

            <button
              onClick={handleSave}
              style={primaryBtn}
            >
              📎 {t('imageFilter.buttons.save')}
            </button>

            <button
              onClick={() =>
                navigate('/gallery-posting', {
                  replace: true,
                  state: {
                    sidebarData: state?.sidebarData ?? undefined,
                    fromEditor: true, // so GalleryPosting can skip reset
                  },
                })
              }
              style={secondaryBtn}
            >
              🔙 {t('imageFilter.buttons.back')}
            </button>
          </div>
        </aside>

        {/* Preview area – smaller image so it fits on one page */}
        <main style={previewArea}>
          <div style={previewCard}>
            {!mediaItem?.file ? (
              <div style={previewMessage}>
                {t('imageFilter.messages.noMedia')}
              </div>
            ) : !imageUrl ? (
              <div style={previewMessage}>
                {t('imageFilter.messages.loading')}
              </div>
            ) : (
              <div style={previewInner}>
                <img
                  src={imageUrl}
                  alt=""
                  ref={imgRef}
                  style={{
                    ...previewImage,
                    filter: cssFilter ?? 'none',
                  }}
                />
                {!!overlay?.tint && (overlay.opacity ?? 0) > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: overlay.tint,
                      opacity: overlay.opacity,
                      mixBlendMode: filterId === 'noir' ? 'multiply' : 'overlay',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
}

/* ---------- styles (aligned & single-page) ---------- */

const pageShell: React.CSSProperties = {
  minHeight: '100vh',
  height: '100vh',
  width: '100%',
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  padding: '12px 24px',
  boxSizing: 'border-box',
  overflow: 'hidden', // 🚫 no global scroll
};

const cardGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '320px minmax(0, 1fr)',
  gap: 18,
  width: '100%',
  maxWidth: 1320,
  height: '100%', // fill viewport height
};

const sidebar: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(4,7,15,0.96), rgba(4,7,15,0.96))',
  borderRadius: 20,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  padding: '14px 14px 12px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const sidebarHeader: React.CSSProperties = {
  marginBottom: 6,
};

const sidebarTitle: React.CSSProperties = {
  fontWeight: 800,
  fontSize: 16,
  marginBottom: 2,
};

const sidebarSubtitle: React.CSSProperties = {
  fontSize: 13,
  color: '#9ca3af',
};

/**
 * Only this area scrolls vertically for filters.
 * Sidebar itself is fixed height due to grid + 100% viewport height.
 */
const filterScrollArea: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  marginTop: 6,
  marginBottom: 10,
  overflowY: 'auto',
  paddingRight: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const sidebarActions: React.CSSProperties = {
  borderTop: '1px solid rgba(15,23,42,0.9)',
  paddingTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flexShrink: 0, // stay fixed at bottom
};

/* Buttons – similar feel to GalleryPosting small actions */
const baseBtn: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 999,
  border: '1px solid #cbd5f5',
  cursor: 'pointer',
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition: 'background-color 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease',
};

const secondaryBtn: React.CSSProperties = {
  ...baseBtn,
  backgroundColor: '#020617',
  color: '#cbd5f5',
};

const primaryBtn: React.CSSProperties = {
  ...baseBtn,
  background: 'linear-gradient(135deg, #facc15, #fbbf24)',
  color: '#111827',
  borderColor: 'rgba(250,204,21,0.9)',
  boxShadow: '0 8px 18px rgba(250,204,21,0.35)',
};

/* Filter tiles */

const filterTile: React.CSSProperties = {
  width: '100%',
  minHeight: 68,
  borderRadius: 16,
  border: '1px solid rgba(148,163,184,0.4)',
  background:
    'radial-gradient(circle at top left, rgba(15,23,42,0.9), rgba(15,23,42,1))',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, background-color 0.15s ease, transform 0.1s ease',
};

const filterTileActive: React.CSSProperties = {
  borderColor: '#ffd76a',
  background:
    'linear-gradient(145deg, rgba(250, 204, 21, 0.12), rgba(15, 23, 42, 0.96))',
};

const filterIconBubble: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 999,
  background: 'radial-gradient(circle at 30% 10%, #1f2937, #020617)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const filterTextCol: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const filterLabelRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
};

const filterName: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#e5e7eb',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const filterHint: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  marginTop: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const filterActiveBadge: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: 'rgba(250, 204, 21, 0.12)',
  color: '#facc15',
  flexShrink: 0,
};

const filterPreviewChip: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 12,
  border: '1px solid rgba(148,163,184,0.6)',
  padding: 2,
  flexShrink: 0,
  boxSizing: 'border-box',
};

const filterPreviewChipInner: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: 10,
  background:
    'linear-gradient(135deg, #9ca3af, #020617)',
  position: 'relative',
  overflow: 'hidden',
};

/* Preview section */

const previewArea: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  minHeight: 0,
};

const previewCard: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(4,7,15,0.96), rgba(3,7,18,0.98))',
  borderRadius: 24,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 22px 50px rgba(0,0,0,0.9)',
  padding: 14,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

/**
 * Make the image a little smaller so everything fits comfortably on one page.
 */
const previewInner: React.CSSProperties = {
  position: 'relative',
  width: '90%',
  height: '90%',          // slightly smaller than full
  maxWidth: '900px',      // cap width
  maxHeight: '90%',       // avoid hitting the edges
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRadius: 18,
  backgroundColor: '#020617',
};

const previewImage: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  display: 'block',
};

const previewMessage: React.CSSProperties = {
  fontSize: 14,
  color: '#9ca3af',
};
